// src/tabs/TreinamentosTab.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Headphones,
  Factory,
  Search,
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronDown,
  BookOpen,
  FileCheck,
  Clock,
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Award,
  Filter,
  Layers,
  Check,
  AlertCircle
} from "lucide-react";
import { TREINAMENTOS_DATA } from "../data/treinamentosData";
import TreinamentoVideoPlayer from "../components/treinamentos/TreinamentoVideoPlayer";
import TreinamentoPopCard from "../components/treinamentos/TreinamentoPopCard";
import TreinamentoMarkdownRenderer from "../components/treinamentos/TreinamentoMarkdownRenderer";

export default function TreinamentosTab({ user }) {
  // Trilha ativa: "atendimento" ou "fabrica"
  const [activeTrilhaId, setActiveTrilhaId] = useState("atendimento");

  // Aula selecionada
  const [selectedAulaId, setSelectedAulaId] = useState("atend-1-1");

  // Filtros de busca
  const [searchQuery, setSearchQuery] = useState("");
  const [onlyPopsFilter, setOnlyPopsFilter] = useState(false);

  // Etapas abertas no accordion lateral
  const [openEtapas, setOpenEtapas] = useState({
    "atend-etapa-1": true,
    "atend-etapa-2": true,
    "atend-etapa-3": true,
    "atend-etapa-4": true,
    "fab-etapa-1": true,
    "fab-etapa-2": true,
    "fab-etapa-3": true,
    "fab-etapa-4": true,
    "fab-etapa-5": true,
    "fab-etapa-6": true,
    "fab-etapa-7": true
  });

  // Progresso persistente no localStorage por usuário
  const storageKey = useMemo(() => {
    return `sj_treinamentos_progresso_${user?.id || "guest"}`;
  }, [user?.id]);

  const [completedAulas, setCompletedAulas] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Salva alterações no localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(completedAulas));
    } catch (e) {
      console.warn("Erro ao salvar progresso de treinamentos:", e);
    }
  }, [completedAulas, storageKey]);

  // Trilha ativa atual
  const currentTrilha = TREINAMENTOS_DATA[activeTrilhaId] || TREINAMENTOS_DATA.atendimento;

  // Lista plana de todas as aulas da trilha para cálculo de progresso e navegação anterior/próxima
  const allAulasInTrilha = useMemo(() => {
    const list = [];
    currentTrilha.etapas.forEach((etapa) => {
      etapa.aulas.forEach((aula) => {
        list.push({ ...aula, etapaTitle: etapa.title, etapaNumber: etapa.number });
      });
    });
    return list;
  }, [currentTrilha]);

  // Aula atualmente selecionada
  const selectedAula = useMemo(() => {
    return (
      allAulasInTrilha.find((a) => a.id === selectedAulaId) ||
      allAulasInTrilha[0] ||
      null
    );
  }, [allAulasInTrilha, selectedAulaId]);

  // Índice da aula selecionada na lista plana
  const currentLessonIndex = useMemo(() => {
    return allAulasInTrilha.findIndex((a) => a.id === selectedAula?.id);
  }, [allAulasInTrilha, selectedAula]);

  // Navegar para aula anterior
  const handlePrevLesson = () => {
    if (currentLessonIndex > 0) {
      setSelectedAulaId(allAulasInTrilha[currentLessonIndex - 1].id);
    }
  };

  // Navegar para próxima aula
  const handleNextLesson = () => {
    if (currentLessonIndex < allAulasInTrilha.length - 1) {
      setSelectedAulaId(allAulasInTrilha[currentLessonIndex + 1].id);
    }
  };

  // Alternar status de conclusão da aula
  const toggleAulaCompleted = (aulaId) => {
    setCompletedAulas((prev) => ({
      ...prev,
      [aulaId]: !prev[aulaId]
    }));
  };

  // Alternar acordeão de etapa
  const toggleEtapaOpen = (etapaId) => {
    setOpenEtapas((prev) => ({
      ...prev,
      [etapaId]: !prev[etapaId]
    }));
  };

  // Cálculo de progresso da trilha ativa
  const progressStats = useMemo(() => {
    const total = allAulasInTrilha.length;
    const completed = allAulasInTrilha.filter((a) => !!completedAulas[a.id]).length;
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, percent };
  }, [allAulasInTrilha, completedAulas]);

  // Troca de trilha (reseta aula selecionada para a primeira da trilha)
  const handleSwitchTrilha = (trilhaId) => {
    setActiveTrilhaId(trilhaId);
    const firstLesson = TREINAMENTOS_DATA[trilhaId]?.etapas[0]?.aulas[0]?.id;
    if (firstLesson) {
      setSelectedAulaId(firstLesson);
    }
  };

  // Filtragem de aulas por busca e filtro de POPs
  const filteredEtapas = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return currentTrilha.etapas
      .map((etapa) => {
        const matchingAulas = etapa.aulas.filter((aula) => {
          if (onlyPopsFilter && !aula.pop) return false;

          if (!query) return true;

          const titleMatch = aula.title.toLowerCase().includes(query);
          const codeMatch = aula.code.toLowerCase().includes(query);
          const popMatch = aula.pop?.title?.toLowerCase().includes(query) || aula.pop?.tag?.toLowerCase().includes(query);
          const contentMatch = aula.content?.toLowerCase().includes(query);
          const pointsMatch = aula.key_points?.some((p) => p.toLowerCase().includes(query));

          return titleMatch || codeMatch || popMatch || contentMatch || pointsMatch;
        });

        return {
          ...etapa,
          aulas: matchingAulas
        };
      })
      .filter((etapa) => etapa.aulas.length > 0);
  }, [currentTrilha, searchQuery, onlyPopsFilter]);

  return (
    <div className="flex flex-col h-full w-full bg-slate-100 overflow-hidden">
      {/* ======================= TOPBAR DA CENTRAL DE TREINAMENTOS ======================= */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 shrink-0 shadow-2xs z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 max-w-7xl mx-auto w-full">
          {/* Título e Seletor de Trilha */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-sm shrink-0">
              <BookOpen size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base font-bold text-slate-900 leading-tight">
                  Central de Treinamentos & POPs
                </h1>
                <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold uppercase tracking-wider">
                  Sempre Joias
                </span>
              </div>
              <p className="text-xs text-slate-500 hidden sm:block">
                Manuais operacionais, procedimentos padrão e roteiros estruturados
              </p>
            </div>
          </div>

          {/* Abas das Trilhas (Atendimento vs Fábrica) */}
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0">
            <button
              type="button"
              onClick={() => handleSwitchTrilha("atendimento")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                activeTrilhaId === "atendimento"
                  ? "bg-white text-blue-700 shadow-xs border border-slate-200/80"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Headphones size={15} className={activeTrilhaId === "atendimento" ? "text-blue-600" : "text-slate-400"} />
              <span>Atendimento & Vendas</span>
              <span className="px-1.5 py-0.2 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">
                4 etapas
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleSwitchTrilha("fabrica")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                activeTrilhaId === "fabrica"
                  ? "bg-white text-emerald-700 shadow-xs border border-slate-200/80"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Factory size={15} className={activeTrilhaId === "fabrica" ? "text-emerald-600" : "text-slate-400"} />
              <span>Fábrica & Produção</span>
              <span className="px-1.5 py-0.2 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                7 etapas
              </span>
            </button>
          </div>

          {/* Barra de Progresso Resumida */}
          <div className="flex items-center gap-3 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-200/80 shrink-0">
            <div className="w-28 sm:w-36">
              <div className="flex justify-between text-[11px] font-bold mb-1">
                <span className="text-slate-600">Progresso</span>
                <span className="text-indigo-600">{progressStats.percent}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${progressStats.percent}%` }}
                />
              </div>
            </div>
            <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">
              {progressStats.completed}/{progressStats.total} aulas
            </span>
          </div>
        </div>
      </header>

      {/* ======================= CORPO PRINCIPAL (SIDEBAR + VIEWER) ======================= */}
      <div className="flex-1 flex overflow-hidden max-w-7xl mx-auto w-full">
        {/* --- SIDEBAR DE ETAPAS E AULAS (ESQUERDA) --- */}
        <aside className="w-80 md:w-88 bg-white border-r border-slate-200 flex flex-col shrink-0 overflow-hidden">
          {/* Caixa de Busca e Filtro */}
          <div className="p-3 border-b border-slate-200 bg-slate-50 space-y-2 shrink-0">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar aula, POP ou termo..."
                className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Toggle de Filtro por POPs */}
            <div className="flex items-center justify-between text-xs px-1">
              <button
                type="button"
                onClick={() => setOnlyPopsFilter(!onlyPopsFilter)}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold transition cursor-pointer ${
                  onlyPopsFilter
                    ? "bg-indigo-600 text-white shadow-2xs"
                    : "bg-slate-200/70 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Filter size={11} />
                <span>Apenas com POPs</span>
              </button>

              <span className="text-[11px] text-slate-400">
                {allAulasInTrilha.length} aulas disponíveis
              </span>
            </div>
          </div>

          {/* Lista de Etapas e Aulas (Accordion) */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {filteredEtapas.length === 0 ? (
              <div className="p-6 text-center text-slate-400">
                <AlertCircle size={28} className="mx-auto text-slate-300 mb-2" />
                <p className="text-xs font-bold text-slate-600">Nenhuma aula encontrada</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Tente alterar os termos de busca ou desativar o filtro de POPs.
                </p>
              </div>
            ) : (
              filteredEtapas.map((etapa) => {
                const isOpen = openEtapas[etapa.id] ?? true;
                const etapaAulasCount = etapa.aulas.length;
                const etapaCompletedCount = etapa.aulas.filter((a) => !!completedAulas[a.id]).length;

                return (
                  <div key={etapa.id} className="rounded-xl border border-slate-200/80 bg-slate-50/50 overflow-hidden">
                    {/* Cabeçalho da Etapa */}
                    <button
                      type="button"
                      onClick={() => toggleEtapaOpen(etapa.id)}
                      className="w-full px-3 py-2.5 flex items-center justify-between text-left hover:bg-slate-100/80 transition cursor-pointer select-none"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-md bg-slate-200 text-slate-700 text-[11px] font-mono font-bold flex items-center justify-center shrink-0">
                          {etapa.number}
                        </span>
                        <div>
                          <h3 className="text-xs font-bold text-slate-800 leading-tight">
                            {etapa.title}
                          </h3>
                          <span className="text-[10px] text-slate-500">
                            {etapaCompletedCount}/{etapaAulasCount} concluídas
                          </span>
                        </div>
                      </div>
                      {isOpen ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                    </button>

                    {/* Lista de Aulas da Etapa */}
                    {isOpen && (
                      <div className="p-1.5 space-y-1 bg-white border-t border-slate-100">
                        {etapa.aulas.map((aula) => {
                          const isSelected = selectedAula?.id === aula.id;
                          const isDone = !!completedAulas[aula.id];

                          return (
                            <button
                              key={aula.id}
                              type="button"
                              onClick={() => setSelectedAulaId(aula.id)}
                              className={`w-full p-2 rounded-xl text-left flex items-start gap-2.5 transition cursor-pointer ${
                                isSelected
                                  ? "bg-indigo-50 border border-indigo-200 text-indigo-950 shadow-2xs"
                                  : "hover:bg-slate-100/70 border border-transparent text-slate-700"
                              }`}
                            >
                              {/* Checkbox de Concluído */}
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAulaCompleted(aula.id);
                                }}
                                className="mt-0.5 shrink-0 text-slate-400 hover:text-indigo-600 transition"
                                title={isDone ? "Marcar como não lida" : "Marcar como concluída"}
                              >
                                {isDone ? (
                                  <CheckCircle2 size={16} className="text-emerald-600 fill-emerald-100" />
                                ) : (
                                  <Circle size={16} className="text-slate-300" />
                                )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-[10px] font-mono font-bold text-slate-400">
                                    {aula.code}
                                  </span>
                                  {aula.pop && (
                                    <span className="px-1.5 py-0.2 rounded-md bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">
                                      {aula.pop.tag || "POP"}
                                    </span>
                                  )}
                                </div>
                                <p className={`text-xs font-medium leading-snug mt-0.5 truncate ${
                                  isSelected ? "font-bold text-indigo-950" : "text-slate-800"
                                }`}>
                                  {aula.title}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* --- VISUALIZADOR DE AULA (CENTRO/DIREITA) --- */}
        <main className="flex-1 bg-slate-50/50 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {selectedAula ? (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Topo do Visualizador: Breadcrumb + Botão de Conclusão */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-200">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-1">
                    <span className="capitalize">{currentTrilha.shortTitle}</span>
                    <span>/</span>
                    <span>Etapa {selectedAula.etapaNumber}</span>
                    <span>/</span>
                    <span className="text-indigo-600 font-bold">{selectedAula.code}</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
                    {selectedAula.title}
                  </h2>
                </div>

                {/* Botão Marcar como Concluído */}
                <button
                  type="button"
                  onClick={() => toggleAulaCompleted(selectedAula.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition shadow-xs cursor-pointer self-start sm:self-center shrink-0 ${
                    completedAulas[selectedAula.id]
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "bg-white border border-slate-300 text-slate-700 hover:border-indigo-500 hover:text-indigo-600"
                  }`}
                >
                  {completedAulas[selectedAula.id] ? (
                    <>
                      <CheckCircle2 size={16} />
                      <span>Aula Concluída ✅</span>
                    </>
                  ) : (
                    <>
                      <Circle size={16} />
                      <span>Marcar como Concluída</span>
                    </>
                  )}
                </button>
              </div>

              {/* Player de Vídeo ou Banner de Guia Escrito */}
              <TreinamentoVideoPlayer
                youtubeId={selectedAula.youtube_id}
                title={selectedAula.title}
                estimatedMinutes={selectedAula.estimated_minutes}
                code={selectedAula.code}
              />

              {/* Pontos-Chave (Key Points) */}
              {selectedAula.key_points && selectedAula.key_points.length > 0 && (
                <div className="p-4 sm:p-5 rounded-2xl bg-indigo-50/80 border border-indigo-100 text-indigo-950 shadow-2xs">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-800 flex items-center gap-1.5 mb-3">
                    <Sparkles size={14} className="text-indigo-600" />
                    <span>Pontos-Chave de Atenção (Leitura em 30 segundos)</span>
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {selectedAula.key_points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm leading-relaxed text-indigo-900">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 mt-2" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Conteúdo Detalhado da Aula */}
              {selectedAula.content && (
                <div className="bg-white p-6 sm:p-7 rounded-2xl border border-slate-200 shadow-2xs">
                  <TreinamentoMarkdownRenderer content={selectedAula.content} />
                </div>
              )}

              {/* Card de POP Operacional (se houver) */}
              {selectedAula.pop && <TreinamentoPopCard pop={selectedAula.pop} />}

              {/* Checklist de Aprendizado & Execução */}
              {selectedAula.checklist && selectedAula.checklist.length > 0 && (
                <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <CheckCircle2 size={15} className="text-indigo-600" />
                    <span>Checklist de Fixação & Verificação</span>
                  </h4>
                  <div className="space-y-2">
                    {selectedAula.checklist.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700 bg-slate-50 p-2.5 rounded-xl border border-slate-200/60">
                        <div className="w-4 h-4 rounded-md border border-slate-300 bg-white flex items-center justify-center shrink-0 mt-0.5">
                          <Check size={11} className="text-indigo-600" />
                        </div>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rodapé de Navegação Entre Aulas (Anterior / Próxima) */}
              <div className="flex items-center justify-between pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handlePrevLesson}
                  disabled={currentLessonIndex <= 0}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition ${
                    currentLessonIndex > 0
                      ? "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 cursor-pointer shadow-2xs"
                      : "opacity-40 bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                  }`}
                >
                  <ArrowLeft size={14} />
                  <span>Aula Anterior</span>
                </button>

                <div className="text-xs text-slate-400 font-medium hidden sm:block">
                  Aula {currentLessonIndex + 1} de {allAulasInTrilha.length}
                </div>

                <button
                  type="button"
                  onClick={handleNextLesson}
                  disabled={currentLessonIndex >= allAulasInTrilha.length - 1}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition ${
                    currentLessonIndex < allAulasInTrilha.length - 1
                      ? "bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer shadow-xs"
                      : "opacity-40 bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
                  }`}
                >
                  <span>Próxima Aula</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 text-center p-8">
              <BookOpen size={48} className="text-slate-300 mb-3" />
              <p className="text-base font-bold text-slate-700">Selecione uma aula na barra lateral</p>
              <p className="text-xs text-slate-500 max-w-sm mt-1">
                Escolha uma etapa para navegar pelos procedimentos e materiais de treinamento.
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
